import * as React from "react";
import { Button, Input, Spinner, tokens } from "@fluentui/react-components";
import { TextT20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../../utils/api";
import { KundePicker, type KundeAuswahl } from "../calendar/KundePicker";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
import { buildMailSubject } from "./mailSubjectSchema";
import { reportMailError } from "../../services/mailError";
import {
    freierBetreffTeil,
    risikoAusVertrag,
    risikoBetreffLabel,
    trenneAntwortPraefix,
    vertragListenLabel,
    type BetreffRisiko,
    type BetreffVertrag,
} from "./betreffAusfuellen";

/**
 * „Ausfüllen" an der Betreffzeile des Composers (PO 27.07.2026).
 *
 * Vertrag und/oder Risikoobjekt des Kontakts wählen — daraus entsteht der
 * Betreff nach dem abgestimmten Schema:
 *
 *     <Kontakt> - VSNR <Vertragsnummer> / <Risikoobjekt> / <Betreff>
 *
 * Bis hierher gab es das Schema nur als Vorbelegung beim Verfassen AUS EINEM
 * VORGANG heraus (AktivitaetCard). Wer die Mail direkt im Postfach schrieb,
 * musste den Betreff von Hand nachbauen — und tat es meist nicht.
 *
 * Der Kontakt ist frei wählbar und aus dem Composer vorbelegt. Ein Vertrag zieht
 * sein Risikoobjekt automatisch mit, sofern genau eines an ihm hängt; bei
 * mehreren wird gefragt statt geraten.
 *
 * Die Vorschau zeigt vor dem Übernehmen, was tatsächlich in der Betreffzeile
 * landet — der Betreff geht an den Kunden, da ist Überraschung teuer.
 */

export type OpenBetreffPickerParams = {
    /** Kontakt aus dem Composer (Mail-Erkennung/Vorgang); frei änderbar. */
    contactId?: number | null;
    /** Aktueller Betreff — der freie Teil daraus wird übernommen. */
    subject: string;
    /** Übernimmt den fertigen Betreff in den Composer. */
    onApply: (subject: string) => void;
};

type Props = OpenBetreffPickerParams & { windowId: string };

const zeileStyle = (aktiv: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "7px 10px",
    borderRadius: 6,
    border: `1px solid ${aktiv ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
    background: aktiv ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground1,
    cursor: "pointer",
    font: "inherit",
    fontSize: 13,
});

const abschnittStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    marginBottom: 6,
};

export const BetreffPickerWindow: React.FC<Props> = ({ windowId, contactId, subject, onApply }) => {
    const { closeWindow } = useWindowManager();

    const [kontakt, setKontakt] = React.useState<KundeAuswahl | null>(null);
    const [vertraege, setVertraege] = React.useState<BetreffVertrag[]>([]);
    const [risiken, setRisiken] = React.useState<BetreffRisiko[]>([]);
    const [vertragId, setVertragId] = React.useState<number | null>(null);
    const [risikoId, setRisikoId] = React.useState<number | null>(null);
    const [betreff, setBetreff] = React.useState(() => freierBetreffTeil(subject));
    const [laden, setLaden] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);

    // Reply-/Forward-Marke bleibt vorn stehen, sonst reißt der Thread.
    const antwortPraefix = React.useMemo(() => trenneAntwortPraefix(subject).praefix, [subject]);

    // Kontakt aus dem Composer nachladen (der Composer kennt nur die Id).
    React.useEffect(() => {
        if (contactId == null) return;
        const ctrl = new AbortController();
        fetch(apiUrl(`contact/${contactId}`), { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((c) => {
                if (!c) return;
                const name =
                    c.displayName ||
                    `${c.lastname ?? ""}${c.firstName ? ", " + c.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim();
                setKontakt({ id: Number(c.id), name: name || `Kontakt ${c.id}` });
            })
            .catch(() => {
                /* Abbruch/Fehler: der Kontakt bleibt wählbar */
            });
        return () => ctrl.abort();
    }, [contactId]);

    // Verträge und Risikoobjekte des gewählten Kontakts.
    React.useEffect(() => {
        setVertragId(null);
        setRisikoId(null);
        if (!kontakt) {
            setVertraege([]);
            setRisiken([]);
            return;
        }
        const ctrl = new AbortController();
        setLaden(true);
        setFehler(null);
        const hole = (pfad: string) =>
            fetch(apiUrl(pfad), { signal: ctrl.signal }).then((r) => (r.ok ? r.json() : []));
        Promise.all([hole(`contracts/by-contact/${kontakt.id}`), hole(`risk-objects/by-contact/${kontakt.id}`)])
            .then(([vs, rs]) => {
                setVertraege(Array.isArray(vs) ? vs : []);
                setRisiken(Array.isArray(rs) ? rs : []);
            })
            .catch((error) => {
                if ((error as Error)?.name === "AbortError") return;
                setFehler(reportMailError({
                    area: "mail",
                    action: "load-linked-mails",
                    error,
                    fallbackMessage: "Verträge und Risikoobjekte konnten nicht geladen werden.",
                }));
            })
            .finally(() => setLaden(false));
        return () => ctrl.abort();
    }, [kontakt]);

    const vertrag = React.useMemo(
        () => vertraege.find((v) => v.id === vertragId) ?? null,
        [vertraege, vertragId],
    );

    // Risikoobjekte zur Auswahl: mit gewähltem Vertrag nur dessen eigene, sonst
    // alle des Kontakts. Sonst stünde im Betreff ein Objekt, das mit der
    // genannten Vertragsnummer nichts zu tun hat.
    const risikoAuswahl = React.useMemo<BetreffRisiko[]>(() => {
        const amVertrag = vertrag?.riskObjects ?? [];
        return amVertrag.length > 0 ? amVertrag : vertrag ? [] : risiken;
    }, [vertrag, risiken]);

    const risiko = React.useMemo(
        () => risikoAuswahl.find((r) => r.id === risikoId) ?? null,
        [risikoAuswahl, risikoId],
    );

    const waehleVertrag = (v: BetreffVertrag) => {
        const abwaehlen = vertragId === v.id;
        setVertragId(abwaehlen ? null : v.id);
        // Hängt genau ein Risikoobjekt am Vertrag, steht es damit fest.
        setRisikoId(abwaehlen ? null : (risikoAusVertrag(v)?.id ?? null));
    };

    const vorschau = React.useMemo(
        () =>
            antwortPraefix +
            buildMailSubject({
                kunde: kontakt?.name,
                vsnr: vertrag?.contractNbr,
                risikoobjekt: risiko ? risikoBetreffLabel(risiko) : null,
                betreff,
            }),
        [antwortPraefix, kontakt, vertrag, risiko, betreff],
    );

    const uebernehmen = () => {
        onApply(vorschau);
        closeWindow(windowId);
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "12px 14px" }}>
                <KundePicker value={kontakt} onChange={setKontakt} optional label="Kontakt" />

                {laden && (
                    <div style={{ padding: "16px 0", display: "flex", justifyContent: "center" }}>
                        <Spinner size="tiny" label="Wird geladen…" />
                    </div>
                )}

                {!laden && kontakt && (
                    <>
                        <div style={{ marginTop: 14 }}>
                            <div style={abschnittStyle}>Vertrag</div>
                            {vertraege.length === 0 ? (
                                <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                                    Keine Verträge zu diesem Kontakt.
                                </div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    {vertraege.map((v) => (
                                        <button
                                            key={v.id}
                                            type="button"
                                            aria-pressed={vertragId === v.id}
                                            style={zeileStyle(vertragId === v.id)}
                                            onClick={() => waehleVertrag(v)}
                                        >
                                            <span style={{ flex: 1, minWidth: 0 }}>{vertragListenLabel(v)}</span>
                                            {(v.riskObjects?.length ?? 0) > 0 && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: tokens.colorNeutralForeground3,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {v.riskObjects!.length} Risikoobjekt
                                                    {v.riskObjects!.length > 1 ? "e" : ""}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: 14 }}>
                            <div style={abschnittStyle}>
                                Risikoobjekt
                                {vertrag && " (zum gewählten Vertrag)"}
                            </div>
                            {risikoAuswahl.length === 0 ? (
                                <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                                    {vertrag
                                        ? "Keine Risikoobjekte an diesem Vertrag."
                                        : "Keine Risikoobjekte zu diesem Kontakt."}
                                </div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    {risikoAuswahl.map((r) => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            aria-pressed={risikoId === r.id}
                                            style={zeileStyle(risikoId === r.id)}
                                            onClick={() => setRisikoId(risikoId === r.id ? null : r.id)}
                                        >
                                            <span style={{ flex: 1, minWidth: 0 }}>{risikoBetreffLabel(r)}</span>
                                            {r.riskType && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: tokens.colorNeutralForeground3,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {r.riskType}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div style={{ marginTop: 14 }}>
                    <div style={abschnittStyle}>Betreff</div>
                    <Input
                        value={betreff}
                        onChange={(_, d) => setBetreff(d.value)}
                        placeholder="worum es geht"
                        style={{ width: "100%" }}
                    />
                </div>

                <div style={{ marginTop: 14 }}>
                    <div style={abschnittStyle}>So sieht die Betreffzeile aus</div>
                    <div
                        style={{
                            padding: "8px 10px",
                            borderRadius: 6,
                            background: tokens.colorNeutralBackground3,
                            fontSize: 13,
                            fontWeight: 600,
                            wordBreak: "break-word",
                            minHeight: 19,
                        }}
                    >
                        {vorschau || <span style={{ fontWeight: 400, color: tokens.colorNeutralForeground3 }}>—</span>}
                    </div>
                </div>

                {fehler && (
                    <div style={{ marginTop: 10, fontSize: 12, color: tokens.colorPaletteRedForeground1 }}>
                        {fehler}
                    </div>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    padding: "10px 14px",
                    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                }}
            >
                <Button appearance="secondary" onClick={() => closeWindow(windowId)}>
                    Abbrechen
                </Button>
                <Button appearance="primary" onClick={uebernehmen} disabled={!vorschau.trim()}>
                    Übernehmen
                </Button>
            </div>
        </div>
    );
};

/** Öffnet die Betreff-Auswahl als WindowManager-Fenster. */
export const openBetreffPicker = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenBetreffPickerParams,
): string => {
    const windowId = `mail-betreff-picker-${Date.now()}`;
    openWindow({
        id: windowId,
        title: "Betreff ausfüllen",
        icon: React.createElement(TextT20Regular),
        initialSize: { width: 520, height: 620 },
        center: true,
        render: () => React.createElement(BetreffPickerWindow, { windowId, ...params }),
    });
    return windowId;
};

export default BetreffPickerWindow;
